<script setup lang="ts">
import { toRaw } from 'vue'
import { dateRange } from '@/helpers/formatDateRange.js'

const { pageType, trackEvent } = useTracking()
const { locale, t } = useI18n()
const { isDesktop } = useBreakpoint()

const emits = defineEmits<{ 'last-search-clicked': [] }>()

const {
  lastSearches,
  goToSearch,
  setData,
  allDestinations,
  PLACEHOLDER_DESTI,
  fetchAllDestinations,
} = useSearchInput()

const lastSearchSnapshot = ref<LastSearchItem[]>([])

const getSearchPhoto = (desitnationId: string) => {
  const desti = allDestinations.value.find(
    (d) => d.id === Number(desitnationId),
  )
  return desti ? desti.first_photo_url : PLACEHOLDER_DESTI
}

const handleLastSearch = (item: LastSearchItem) => {
  setData('location', {
    type: 'destination',
    ...item,
  })
  trackEvent({
    event: 'previous_search_click',
    source: pageType.value,
  })
  if (item.startDate && item.endDate) {
    setData('endDate', item.endDate)
    setData('startDate', item.startDate)
  } else {
    setData('endDate', null)
    setData('startDate', null)
  }
  setData('guest', item.guest || 0)
  goToSearch()

  emits('last-search-clicked')
}

const dateRangeFormatted = ({ startDate, endDate }: LastSearchItem) => {
  return dateRange({
    i18n: { locale, t },
    startDate,
    endDate,
  })
}

onMounted(async () => {
  lastSearchSnapshot.value = toRaw(lastSearches.value)
  if (!allDestinations.value?.length && !isDesktop.value)
    await fetchAllDestinations()
})
</script>

<template>
  <div v-if="lastSearchSnapshot.length" class="mt-6 laptop:mt-0">
    <div class="mb-2 text-left text-sm font-bold laptop:px-1 laptop:text-xs">
      {{ t('search.lastSearch') }}
    </div>

    <div class="flex overflow-hidden overflow-x-auto laptop:block">
      <div
        v-for="item in lastSearchSnapshot"
        :key="item.id"
        :class="[
          'flex cursor-pointer text-left laptop:w-full laptop:items-center laptop:px-1 laptop:hover:bg-beige',
          lastSearchSnapshot.length === 1
            ? 'w-full flex-auto'
            : 'w-[200px] flex-none',
        ]"
        @click.stop="handleLastSearch(item)"
      >
        <NuxtImg
          v-if="!isDesktop"
          :src="getSearchPhoto(item.id)"
          class="rounded-sm"
          height="60"
          loading="lazy"
          width="60"
        />
        <BaseIcon v-else name="search" class="mr-4 flex-none" />
        <div
          class="flex flex-col justify-center truncate p-2 text-md laptop:p-0 laptop:py-1"
        >
          <span class="truncate">
            {{ item.name }}
          </span>
          <div
            v-if="item.startDate && item.endDate"
            class="truncate text-xs text-gray-500 laptop:text-sm"
          >
            {{ dateRangeFormatted(item) }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
